import { useState, useCallback } from 'react';
import { useToast } from '../../../components/ui/ToastProvider';
import api from '../../../utils/api';
import { areaDraftType } from '../utils/parcelSelection';

export function useMapFeatures() {
  const { show } = useToast();
  const [features, setFeatures] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [draftFeature, setDraftFeature] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  
  // Draft state
  const [draftName, setDraftName] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftIcon, setDraftIcon] = useState('default');
  const [draftRadius, setDraftRadius] = useState(0);
  const [draftOptimalWinds, setDraftOptimalWinds] = useState([]);
  const [draftIsStand, setDraftIsStand] = useState(false);
  const [draftLineColor, setDraftLineColor] = useState('');
  const [draftFillColor, setDraftFillColor] = useState('');
  const [draftFillOpacity, setDraftFillOpacity] = useState(0);
  
  // Edit state
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editIcon, setEditIcon] = useState('default');
  const [editRadius, setEditRadius] = useState(0);
  const [editOptimalWind, setEditOptimalWind] = useState('');
  const [editOptimalWinds, setEditOptimalWinds] = useState([]);
  const [editIsStand, setEditIsStand] = useState(false);
  const [editLineColor, setEditLineColor] = useState('');
  const [editFillColor, setEditFillColor] = useState('');
  const [editFillOpacity, setEditFillOpacity] = useState(0);
  // Feeding (personal features)
  const [editFeedingMode, setEditFeedingMode] = useState(''); // ''|'hopper'|'ground'
  const [editFeedCapacityLbs, setEditFeedCapacityLbs] = useState(null);
  const [editConsumptionRateCode, setEditConsumptionRateCode] = useState(''); // low|avg|high|very_high
  const [editRefillThresholdPct, setEditRefillThresholdPct] = useState(20);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);

  const listFeatures = useCallback(async () => {
    try {
      const res = await api.get('/features/');
      console.log('Features loaded:', res.data);
      console.log('Club boundaries:', res.data.filter(f => f.is_club_boundary));
      // Exclude club-shared items from personal view
      const filtered = Array.isArray(res.data)
        ? res.data.filter(f => {
            try {
              if (f && (f.share_type === 'club')) return false;
              // Some APIs may include nested shared_with_club object
              if (f && f.shared_with_club) return false;
            } catch (_) {}
            return true;
          })
        : [];
      setFeatures(filtered);
      return res.data;
    } catch (e) {
      console.error('List failed:', e);
      if (e.response?.status !== 401) {
        alert('List failed');
      }
      throw e;
    }
  }, []);

  const handleFeatureCreate = useCallback((feature) => {
    setDraftFeature({ tempId: feature.id, geometry: feature.geometry, type: areaDraftType(feature.geometry) });
    setDraftName('');
    setDraftNotes('');
    setDraftIcon('default');
    setDraftRadius(0);
    setDraftOptimalWinds([]);
    setDraftIsStand(false);
    setShowCreateModal(true);
  }, []);

  const handleCancelCreate = useCallback(() => {
    setShowCreateModal(false);
    setDraftFeature(null);
    setDraftName('');
    setDraftNotes('');
    setDraftIcon('default');
    setDraftRadius(0);
    setDraftOptimalWinds([]);
    setDraftIsStand(false);
  }, []);

  const handleSaveCreate = useCallback(async (isClubBoundary = false, clubId = null) => {
    if (!draftFeature) return;
    
    try {
      const saveData = {
        type: draftFeature.type,
        geojson: draftFeature.geometry,
        name: draftName || 'Unnamed',
        notes: draftNotes,
        share: false,
        icon: draftIcon,
        radius: draftRadius > 0 ? draftRadius : null,
      };
      if (draftFeature.type === 'polygon') {
        if (draftLineColor) saveData.line_color = draftLineColor;
        if (draftFillColor) saveData.fill_color = draftFillColor;
        if (typeof draftFillOpacity === 'number') saveData.fill_opacity = draftFillOpacity;
      }
      // Include is_stand and optimal winds for point features (personal map)
      if (draftFeature.type === 'point') {
        saveData.is_stand = Boolean(draftIsStand);
        saveData.optimal_winds = draftIsStand ? (Array.isArray(draftOptimalWinds) ? draftOptimalWinds : []) : [];
      }
      
      // Add club boundary information if provided
      if (isClubBoundary) {
        saveData.is_club_boundary = true;
      }
      
      await api.post('/features/', saveData);
      
      // Refresh from server to ensure latest values
      await listFeatures();
      setShowCreateModal(false);
      setDraftFeature(null);
      setDraftName('');
      setDraftNotes('');
      setDraftIcon('default');
      setDraftRadius(0);
      setDraftOptimalWinds([]);
      setDraftIsStand(false);
      
    } catch (e) {
      console.error('Create failed:', e);
      if (e.response?.status !== 401) {
        alert('Create failed');
      }
      throw e;
    }
  }, [draftFeature, draftName, draftNotes, draftIcon, draftRadius, draftOptimalWinds, draftIsStand, draftLineColor, draftFillColor, draftFillOpacity, listFeatures]);

  const handleEditFeature = useCallback((feature) => {
    setEditingFeature(feature);
    setEditName(feature.name || '');
    setEditNotes(feature.notes || '');
    setEditIcon(feature.icon || 'default');
    setEditRadius(feature.radius || 0);
    setEditOptimalWind(feature.optimal_wind || '');
    // Stand: explicit is_stand or fallback to "has optimal winds" for backward compat
    const isStand = feature.is_stand ?? !!(feature.optimal_winds?.length || feature.optimal_wind);
    setEditIsStand(isStand);
    if (feature && Array.isArray(feature.optimal_winds) && feature.optimal_winds.length > 0) {
      setEditOptimalWinds(feature.optimal_winds);
    } else if (feature && feature.optimal_wind) {
      setEditOptimalWinds([feature.optimal_wind]);
    } else {
      setEditOptimalWinds([]);
    }
    setEditLineColor(feature.line_color || feature.lineColor || '');
    setEditFillColor(feature.fill_color || feature.fillColor || '');
    setEditFillOpacity(typeof feature.fill_opacity === 'number' ? feature.fill_opacity : (typeof feature.fillOpacity === 'number' ? feature.fillOpacity : 0));
    // Feeding
    setEditFeedingMode(feature.feeding_mode || '');
    setEditFeedCapacityLbs(feature.feed_capacity_lbs || null);
    setEditConsumptionRateCode(feature.consumption_rate_code || '');
    setEditRefillThresholdPct(typeof feature.refill_threshold_pct === 'number' ? feature.refill_threshold_pct : 20);
    setShowEditModal(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setShowEditModal(false);
    setEditingFeature(null);
    setEditName('');
    setEditNotes('');
    setEditIcon('default');
    setEditRadius(0);
    setEditOptimalWind('');
    setEditOptimalWinds([]);
    setEditIsStand(false);
    setEditFeedingMode('');
    setEditFeedCapacityLbs(null);
    setEditConsumptionRateCode('');
    setEditRefillThresholdPct(20);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingFeature) return;
    
    try {
      const updateData = {
        name: editName || 'Unnamed',
        notes: editNotes,
        icon: editIcon,
        radius: editRadius > 0 ? editRadius : null,
      };
      if (editingFeature?.geojson?.type === 'Polygon' || editingFeature?.geojson?.type === 'MultiPolygon' || editingFeature?.type === 'polygon') {
        if (editLineColor) updateData.line_color = editLineColor;
        if (editFillColor) updateData.fill_color = editFillColor;
        if (typeof editFillOpacity === 'number') updateData.fill_opacity = editFillOpacity;
      }
      // Include is_stand and optimal winds for point features
      if (editingFeature?.geojson?.type === 'Point' || editingFeature?.type === 'point') {
        updateData.is_stand = Boolean(editIsStand);
        updateData.optimal_winds = editIsStand ? (Array.isArray(editOptimalWinds) ? editOptimalWinds : []) : [];
        // Feeding fields
        if (!editFeedingMode) {
          // Disabling feeding: explicitly clear feeding fields
          updateData.feeding_mode = null;
          updateData.feed_capacity_lbs = null;
          updateData.consumption_rate_code = null;
          // Do not send threshold when disabling; leave existing value unused
        } else {
          updateData.feeding_mode = editFeedingMode;
          updateData.feed_capacity_lbs = editFeedingMode === 'hopper' ? (editFeedCapacityLbs || null) : null;
          updateData.consumption_rate_code = editConsumptionRateCode || null;
          // Only send threshold when hopper; else omit
          if (editFeedingMode === 'hopper' && typeof editRefillThresholdPct === 'number') {
            updateData.refill_threshold_pct = editRefillThresholdPct;
          }
        }
      }
      
      await api.patch(`/features/${editingFeature.id}/`, updateData);
      
      // Refresh from server to ensure latest values
      await listFeatures();
      
      setShowEditModal(false);
      setEditingFeature(null);
      setEditName('');
      setEditNotes('');
      setEditIcon('default');
      setEditRadius(0);
      setEditOptimalWind('');
      setEditOptimalWinds([]);
      setEditIsStand(false);
      setEditFeedingMode('');
      setEditFeedCapacityLbs(null);
      setEditConsumptionRateCode('');
      setEditRefillThresholdPct(20);
      
    } catch (e) {
      console.error('Update failed:', e);
      if (e.response?.status !== 401) {
        alert('Update failed');
      }
      throw e;
    }
  }, [editingFeature, editName, editNotes, editIcon, editRadius, editOptimalWinds, editIsStand, editLineColor, editFillColor, editFillOpacity, listFeatures, editFeedingMode, editFeedCapacityLbs, editConsumptionRateCode, editRefillThresholdPct]);

  const handleDeleteFeature = useCallback(async (featureId) => {
    try {
      await api.delete(`/features/${featureId}/`);
      setFeatures(prev => prev.filter(f => f.id !== featureId));
    } catch (e) {
      console.error('Delete failed:', e);
      if (e.response?.status !== 401) {
        alert('Delete failed');
      }
      throw e;
    }
  }, []);

  const handleDeleteRequest = useCallback((featureOrId) => {
    // Handle both feature object and feature ID
    let feature;
    if (typeof featureOrId === 'number' || typeof featureOrId === 'string') {
      // It's a feature ID, find the feature in our state
      feature = features.find(f => f.id === parseInt(featureOrId));
      if (!feature) {
        console.error('Feature not found for ID:', featureOrId);
        return;
      }
    } else {
      // It's a feature object
      feature = featureOrId;
    }
    
    setFeatureToDelete(feature);
    setShowDeleteModal(true);
  }, [features]);

  const handleConfirmDelete = useCallback(async () => {
    if (!featureToDelete) return;
    
    try {
      await handleDeleteFeature(featureToDelete.id);
      try {
        const title = 'Feature deleted';
        const body = featureToDelete?.name ? `"${featureToDelete.name}" was removed.` : 'The feature was removed.';
        show({ title, body, variant: 'info' });
      } catch (_) {}
      setShowDeleteModal(false);
      setFeatureToDelete(null);
    } catch (e) {
      // Error already handled in handleDeleteFeature
    }
  }, [featureToDelete, handleDeleteFeature, show]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setFeatureToDelete(null);
  }, []);

  return {
    features,
    setFeatures,
    showCreateModal,
    showEditModal,
    draftFeature,
    editingFeature,
    draftName,
    setDraftName,
    draftNotes,
    setDraftNotes,
    draftIcon,
    setDraftIcon,
    draftRadius,
    setDraftRadius,
    draftOptimalWinds,
    setDraftOptimalWinds,
    draftIsStand,
    setDraftIsStand,
    draftLineColor,
    setDraftLineColor,
    draftFillColor,
    setDraftFillColor,
    draftFillOpacity,
    setDraftFillOpacity,
    editName,
    setEditName,
    editNotes,
    setEditNotes,
    editIcon,
    setEditIcon,
    editRadius,
    setEditRadius,
    editOptimalWind,
    setEditOptimalWind,
    editOptimalWinds,
    setEditOptimalWinds,
    editIsStand,
    setEditIsStand,
    editLineColor,
    setEditLineColor,
    editFillColor,
    setEditFillColor,
    editFillOpacity,
    setEditFillOpacity,
    editFeedingMode,
    setEditFeedingMode,
    editFeedCapacityLbs,
    setEditFeedCapacityLbs,
    editConsumptionRateCode,
    setEditConsumptionRateCode,
    editRefillThresholdPct,
    setEditRefillThresholdPct,
    listFeatures,
    handleFeatureCreate,
    handleCancelCreate,
    handleSaveCreate,
    handleEditFeature,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteFeature,
    handleDeleteRequest,
    handleConfirmDelete,
    handleCancelDelete,
    showDeleteModal,
    featureToDelete,
  };
}
